/**
 * ProgramTypeDetailPage - Detailansicht/Neuanlage eines Programmtyps
 * 
 * Sektionen:
 * - Grunddaten (Name)
 * - Darstellung (Farbe/Badge)
 * - Technisch (Code - read-only, auto-generiert)
 * - Hinweise
 * - Gefahrenzone (Deaktivieren)
 * 
 * UX-Leitlinie: Code ist technisch, nicht User-relevant
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Tag, ArrowLeft, RefreshCw, Save, Trash2, AlertTriangle, Copy, ChevronDown, ChevronRight } from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';

type ProgramTypeColor = 'GREEN' | 'ORANGE' | 'PURPLE' | 'BLUE' | 'GRAY';

interface ProgramType {
  id: string;
  code: string;
  name: string;
  color: ProgramTypeColor;
  isActive: boolean;
}

interface FormData {
  name: string;
  color: ProgramTypeColor;
}

const COLOR_OPTIONS: { value: ProgramTypeColor; label: string; classes: string }[] = [
  { value: 'GREEN', label: 'Grün (Standard)', classes: 'bg-green-100 text-green-800' },
  { value: 'ORANGE', label: 'Orange (Nachwäsche)', classes: 'bg-orange-100 text-orange-800' },
  { value: 'PURPLE', label: 'Lila (Sonderwäsche)', classes: 'bg-purple-100 text-purple-800' },
  { value: 'BLUE', label: 'Blau (Neue Wäsche)', classes: 'bg-blue-100 text-blue-800' },
  { value: 'GRAY', label: 'Grau (Neutral)', classes: 'bg-gray-100 text-gray-800' },
];

const getColorClasses = (color: ProgramTypeColor): string => {
  return COLOR_OPTIONS.find(c => c.value === color)?.classes || 'bg-gray-100 text-gray-800';
};

const INITIAL_FORM: FormData = {
  name: '',
  color: 'GREEN',
};

export default function ProgramTypeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = id === 'new';
  
  const [programType, setProgramType] = useState<ProgramType | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Technical section collapsed state
  const [techOpen, setTechOpen] = useState(false);
  
  // Delete Confirm State
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Load program type details (only for edit mode)
  const loadProgramType = async () => {
    if (isNew || !id) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/admin/program-types/${id}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setProgramType(data);
        setFormData({
          name: data.name,
          color: data.color,
        });
      } else if (res.status === 404) {
        navigate('/admin/program-types');
      }
    } catch (err) {
      console.error('Fehler beim Laden des Programmtyps:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!isNew) {
      loadProgramType();
    }
  }, [id, isNew]);

  // Copy to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  // Save (create or update)
  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Name ist erforderlich');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const url = isNew ? '/api/admin/program-types' : `/api/admin/program-types/${id}`;
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: formData.name.trim(),
          color: formData.color,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        if (isNew) {
          navigate(`/admin/program-types/${data.id}`);
        } else {
          loadProgramType();
        }
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || data.message || 'Fehler beim Speichern');
      }
    } catch (err) {
      setError('Netzwerkfehler');
    }
    setSaving(false);
  };

  // Delete (soft delete)
  const handleDelete = async () => {
    if (!id || isNew) return;
    
    setDeleting(true);
    try {
      const res = await fetch(`/api/admin/program-types/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (res.ok) {
        navigate('/admin/program-types');
      }
    } catch (err) {
      console.error('Fehler beim Deaktivieren:', err);
    }
    setDeleting(false);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-8 text-center text-slate-500">Laden...</div>
      </AdminLayout>
    );
  }

  if (!isNew && !programType) {
    return (
      <AdminLayout>
        <div className="p-8 text-center text-slate-500">Programmtyp nicht gefunden</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <Link 
              to="/admin/program-types" 
              className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1 mb-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Zurück zur Übersicht
            </Link>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Tag className="w-6 h-6" />
              {isNew ? 'Neuer Programmtyp' : programType?.name}
              {!isNew && programType && (
                <span className="text-sm font-mono text-slate-400">({programType.code})</span>
              )}
            </h1>
            {!isNew && programType && !programType.isActive && (
              <p className="text-sm text-red-500 mt-1">(inaktiv)</p>
            )}
          </div>
          <div className="flex gap-2">
            {!isNew && (
              <button
                onClick={() => loadProgramType()}
                disabled={loading}
                className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
                title="Aktualisieren"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={saving || !formData.name.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Speichern...' : 'Speichern'}
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {/* Form Sections */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Grunddaten */}
          <section className="bg-white rounded-lg border p-6 space-y-4">
            <h2 className="font-semibold text-slate-900 border-b pb-2">Grunddaten</h2>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="z.B. Standard"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
            </div>
            
          </section>

          {/* Darstellung */}
          <section className="bg-white rounded-lg border p-6 space-y-4">
            <h2 className="font-semibold text-slate-900 border-b pb-2">Darstellung</h2>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Farbe</label>
              <select
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value as ProgramTypeColor })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {COLOR_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Vorschau</label>
              <span className={`inline-flex px-3 py-1.5 text-sm rounded ${getColorClasses(formData.color)}`}>
                {formData.name || 'Beispiel'}
              </span>
            </div>
          </section>

          {/* Hinweise */}
          <section className="bg-slate-50 rounded-lg border p-6 space-y-4">
            <h2 className="font-semibold text-slate-700">Hinweise</h2>
            <p className="text-sm text-slate-600">
              Programmtypen kategorisieren Programme für eine bessere Übersicht. 
              Das Deaktivieren eines Typs beeinflusst bereits erfasste Daten nicht, 
              da diese Snapshot-Informationen enthalten.
            </p>
          </section>

          {/* Technisch (nur bei Edit) */}
          {!isNew && programType && (
            <section className="bg-white rounded-lg border p-4">
              <button
                onClick={() => setTechOpen(!techOpen)}
                className="w-full flex items-center justify-between text-left"
              >
                <h2 className="font-medium text-slate-500 text-sm">Technische Details</h2>
                {techOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
              </button>
              {techOpen && (
                <div className="mt-3 pt-3 border-t space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">Code</span>
                    <div className="flex items-center gap-2">
                      <code className="text-sm font-mono bg-slate-100 px-2 py-1 rounded">{programType.code}</code>
                      <button
                        onClick={() => copyToClipboard(programType.code)}
                        className="p-1 text-slate-400 hover:text-slate-600 rounded"
                        title="Code kopieren"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">ID</span>
                    <div className="flex items-center gap-2">
                      <code className="text-sm font-mono bg-slate-100 px-2 py-1 rounded text-xs">{programType.id}</code>
                      <button
                        onClick={() => copyToClipboard(programType.id)}
                        className="p-1 text-slate-400 hover:text-slate-600 rounded"
                        title="ID kopieren"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </section>
          )}
        </div>

        {/* Delete Section (nur bei Edit) */}
        {!isNew && programType && (
          <section className="bg-white rounded-lg border border-red-200 p-6">
            <h2 className="font-semibold text-red-600 mb-2">Gefahrenzone</h2>
            <p className="text-sm text-slate-600 mb-4">
              Das Deaktivieren eines Programmtyps entfernt ihn aus der Auswahl. 
              Bereits zugeordnete Programme behalten ihre Typ-Information.
            </p>
            <button
              onClick={() => setDeleteConfirmOpen(true)}
              className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Programmtyp deaktivieren
            </button>
          </section>
        )}
      </div>

      {/* Delete Confirm Modal */}
      {deleteConfirmOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
            <div className="p-4 border-b flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-red-600" />
              <h2 className="text-lg font-semibold text-red-600">Programmtyp deaktivieren?</h2>
            </div>
            <div className="p-4">
              <p className="text-slate-600">
                <strong>"{programType?.name}"</strong> ({programType?.code}) wird deaktiviert.
              </p>
              <p className="text-sm text-slate-500 mt-2">
                Der Typ kann nicht mehr für neue Programme verwendet werden.
              </p>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t bg-slate-50 rounded-b-xl">
              <button
                onClick={() => setDeleteConfirmOpen(false)}
                className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-100"
              >
                Abbrechen
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Deaktivieren...' : 'Deaktivieren'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
